import { useEffect, useRef, useState, useCallback } from 'react'
import {
  initCornerstone,
  cornerstone,
  cornerstoneTools,
} from '../services/cornerstoneSetup'

const { Enums: csEnums, RenderingEngine } = cornerstone
const { Enums: toolEnums, ToolGroupManager } = cornerstoneTools

const RENDERING_ENGINE_ID = 'studioRenderingEngine'
const VIEWPORT_ID = 'studioViewport'
const TOOL_GROUP_ID = 'studioToolGroup'

const TOOLS = {
  windowLevel: { name: 'WindowLevel', label: 'W/L', icon: '◐' },
  pan: { name: 'Pan', label: 'Pan', icon: '✥' },
  zoom: { name: 'Zoom', label: 'Zoom', icon: '⊕' },
  length: { name: 'Length', label: 'Length', icon: '📏' },
  angle: { name: 'Angle', label: 'Angle', icon: '∠' },
  rotate: { name: 'PlanarRotate', label: 'Rotate', icon: '⟳' },
}

export default function DicomViewer({ dicomFiles }) {
  const viewportRef = useRef(null)
  const engineRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [totalSlices, setTotalSlices] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadProgress, setLoadProgress] = useState(0)
  const [error, setError] = useState(null)
  const [imageInfo, setImageInfo] = useState(null)
  const [activeTool, setActiveTool] = useState('windowLevel')
  const [inverted, setInverted] = useState(false)

  const switchTool = useCallback((toolKey) => {
    const toolGroup = ToolGroupManager.getToolGroup(TOOL_GROUP_ID)
    if (!toolGroup) return

    // Set all primary-button tools to passive first
    Object.values(TOOLS).forEach(({ name }) => {
      try { toolGroup.setToolPassive(name) } catch (e) { /* ignore */ }
    })

    // Activate selected tool on left mouse button
    const toolName = TOOLS[toolKey].name
    toolGroup.setToolActive(toolName, {
      bindings: [{ mouseButton: toolEnums.MouseBindings.Primary }],
    })

    // Keep scroll on wheel always
    try {
      toolGroup.setToolActive(cornerstoneTools.StackScrollTool.toolName, {
        bindings: [{ mouseButton: toolEnums.MouseBindings.Wheel }],
      })
    } catch (e) { /* ignore */ }

    setActiveTool(toolKey)
  }, [])

  const toggleInvert = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    const viewport = engine.getViewport(VIEWPORT_ID)
    if (!viewport) return
    const { invert } = viewport.getProperties()
    viewport.setProperties({ invert: !invert })
    viewport.render()
    setInverted(!invert)
  }, [])

  const flipH = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    const viewport = engine.getViewport(VIEWPORT_ID)
    if (!viewport) return
    const { flipHorizontal } = viewport.getProperties()
    viewport.setProperties({ flipHorizontal: !flipHorizontal })
    viewport.render()
  }, [])

  const flipV = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    const viewport = engine.getViewport(VIEWPORT_ID)
    if (!viewport) return
    const { flipVertical } = viewport.getProperties()
    viewport.setProperties({ flipVertical: !flipVertical })
    viewport.render()
  }, [])

  const resetView = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    const viewport = engine.getViewport(VIEWPORT_ID)
    if (!viewport) return
    viewport.resetCamera()
    viewport.resetProperties()
    viewport.render()
    setInverted(false)
  }, [])

  const loadDicomStack = useCallback(async () => {
    if (!dicomFiles || dicomFiles.length === 0) return

    try {
      setLoading(true)
      setError(null)

      await initCornerstone()

      const imageIds = dicomFiles.map((file) => `wadouri:${file.url}`)
      setTotalSlices(imageIds.length)

      if (engineRef.current) {
        engineRef.current.destroy()
      }
      const renderingEngine = new RenderingEngine(RENDERING_ENGINE_ID)
      engineRef.current = renderingEngine

      const viewportInput = {
        viewportId: VIEWPORT_ID,
        type: csEnums.ViewportType.STACK,
        element: viewportRef.current,
      }
      renderingEngine.enableElement(viewportInput)

      // Set up tool group
      let toolGroup = ToolGroupManager.getToolGroup(TOOL_GROUP_ID)
      if (toolGroup) {
        ToolGroupManager.destroyToolGroup(TOOL_GROUP_ID)
      }
      toolGroup = ToolGroupManager.createToolGroup(TOOL_GROUP_ID)
      toolGroup.addViewport(VIEWPORT_ID, RENDERING_ENGINE_ID)

      toolGroup.addTool(cornerstoneTools.WindowLevelTool.toolName)
      toolGroup.addTool(cornerstoneTools.PanTool.toolName)
      toolGroup.addTool(cornerstoneTools.ZoomTool.toolName)
      toolGroup.addTool(cornerstoneTools.StackScrollTool.toolName)
      toolGroup.addTool(cornerstoneTools.LengthTool.toolName)
      toolGroup.addTool(cornerstoneTools.AngleTool.toolName)
      toolGroup.addTool(cornerstoneTools.PlanarRotateTool.toolName)

      // Default: W/L on left click, scroll on wheel
      toolGroup.setToolActive(cornerstoneTools.WindowLevelTool.toolName, {
        bindings: [{ mouseButton: toolEnums.MouseBindings.Primary }],
      })
      toolGroup.setToolActive(cornerstoneTools.StackScrollTool.toolName, {
        bindings: [{ mouseButton: toolEnums.MouseBindings.Wheel }],
      })

      const viewport = renderingEngine.getViewport(VIEWPORT_ID)
      await viewport.setStack(imageIds)

      // Prefetch images
      let loaded = 0
      for (const imageId of imageIds) {
        try {
          await cornerstone.imageLoader.loadImage(imageId)
          loaded++
          setLoadProgress(Math.round((loaded / imageIds.length) * 100))
        } catch (e) {
          loaded++
          setLoadProgress(Math.round((loaded / imageIds.length) * 100))
        }
      }

      // Listen for slice changes — cornerstone3D v2 uses imageIdIndex
      viewportRef.current.addEventListener(
        csEnums.Events.STACK_NEW_IMAGE,
        (evt) => {
          const idx = evt.detail?.imageIdIndex
          if (idx != null) setCurrentIndex(idx)
        }
      )

      // Extract metadata from first image
      try {
        const image = await cornerstone.imageLoader.loadImage(imageIds[0])
        setImageInfo({
          rows: image.rows,
          columns: image.columns,
          sliceThickness: image.data?.string?.('x00180050') || 'N/A',
          pixelSpacing: image.data?.string?.('x00280030') || 'N/A',
          modality: image.data?.string?.('x00080060') || 'N/A',
          studyDescription: image.data?.string?.('x00081030') || 'N/A',
        })
      } catch (e) {
        // metadata extraction is best-effort
      }

      viewport.render()
      setLoading(false)
    } catch (err) {
      console.error('DICOM load error:', err)
      setError(err.message)
      setLoading(false)
    }
  }, [dicomFiles])

  useEffect(() => {
    loadDicomStack()

    return () => {
      if (engineRef.current) {
        engineRef.current.destroy()
        engineRef.current = null
      }
    }
  }, [loadDicomStack])

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          {/* Tool buttons */}
          {Object.entries(TOOLS).map(([key, tool]) => (
            <button
              key={key}
              onClick={() => switchTool(key)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                activeTool === key
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              title={tool.label}
            >
              <span className="mr-1.5">{tool.icon}</span>
              {tool.label}
            </button>
          ))}

          <div className="w-px h-6 bg-gray-600 mx-1" />

          <button
            onClick={toggleInvert}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              inverted
                ? 'bg-brand-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            title="Invert (negative)"
          >
            ◑ Invert
          </button>

          <button
            onClick={flipH}
            className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            title="Flip horizontal"
          >
            ⇔ Flip H
          </button>

          <button
            onClick={flipV}
            className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            title="Flip vertical"
          >
            ⇕ Flip V
          </button>

          <div className="w-px h-6 bg-gray-600 mx-1" />

          <button
            onClick={resetView}
            className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            title="Reset all (view, invert, flip)"
          >
            ↺ Reset
          </button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400">
            Slice {currentIndex + 1} / {totalSlices}
          </span>
          {loading && (
            <span className="text-sm text-amber-400">
              Loading... {loadProgress}%
            </span>
          )}
        </div>
      </div>

      {/* Viewport */}
      <div className="flex-1 relative bg-black">
        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="card text-red-400 max-w-md">
              <p className="font-medium">Failed to load DICOM</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        )}
        <div
          ref={viewportRef}
          className="w-full h-full"
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>

      {/* Info bar */}
      {imageInfo && (
        <div className="flex items-center gap-6 px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400">
          <span>Matrix: {imageInfo.rows} x {imageInfo.columns}</span>
          <span>Modality: {imageInfo.modality}</span>
          <span>Slice Thickness: {imageInfo.sliceThickness}</span>
          <span>Pixel Spacing: {imageInfo.pixelSpacing}</span>
          {imageInfo.studyDescription && (
            <span>Study: {imageInfo.studyDescription}</span>
          )}
        </div>
      )}
    </div>
  )
}
